import React, { useState } from 'react';
import Divider from 'src/components/ui/Divider/Divider';
import classNames from 'classnames';
import OptionItemList from 'src/components/ui/OptionItemList/OptionItemList';
import { OptionItemProps, OptionItemTone } from 'src/components/ui/OptionItem/OptionItem';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLogout } from 'src/services/session';
import { menuOverlayItemsFirstSection, menuOverlayItemsSecondSection } from '../routes';
import styles from './NavOverlay.module.scss';
import NotificationCenterModal from '../../../modals/NotificationCenterModal/NotificationCenterModal';
import useNotifications from '../../../../services/notification';
import { getTermsByType } from '../../../../services/terms';
import { useNavigationItems } from '../../../../hooks/useNavigationItems';
import { APP_VERSION } from '../../../../constants';

export type NavOverlayProps = {
  onOptionSelect: () => void;
  expanded?: boolean;
};

export const NavOverlay: React.FC<NavOverlayProps> = ({ onOptionSelect, expanded }) => {
  const { t } = useTranslation();
  const logout = useLogout();

  // No `link` on purpose: with one, OptionItem renders a <NavLink to="/login">
  // whose own click handler navigates *again* after this onClick already did,
  // pushing two history entries for /login. Without it, OptionItem renders a
  // plain <button> (identical styling) and logout() owns the navigation.
  const logoutMenuItem = {
    accessory: 'icon',
    accessoryItem: 'logOut',
    label: 'Abmelden',
    tone: OptionItemTone.Destructive,
    onClick: logout,
  } as OptionItemProps;

  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState<boolean>(false);
  const [hideMenu, setHideMenu] = useState<boolean>(false);
  const { fetchUnreadCount } = useNotifications();
  const { findAllAccessRightsByRoute, findAccessRights } = useNavigationItems();
  const accessRights = findAllAccessRightsByRoute('settings');
  const hasFulfillmentBillingAccess = accessRights?.some(
    r => r.route.includes('fulfillment_billing') && (r.read || r.read_write),
  );

  const invoicesAccess = findAccessRights('invoices');
  const hasInvoicesAccess = !!invoicesAccess && (invoicesAccess.read || invoicesAccess.read_write);

  const filteredFirstSection = menuOverlayItemsFirstSection.filter(item => {
    if (item.link === 'settings?tab=fulfillment_billing') return hasFulfillmentBillingAccess;
    if (item.link === 'invoices') return hasInvoicesAccess;
    return true;
  });
  const secondSection = menuOverlayItemsSecondSection.map(item =>
    item.type === 'version' ? { ...item, label: `Weship ${APP_VERSION}` } : item,
  );

  const openNotifications = () => {
    setHideMenu(true);
    setIsNotificationsModalOpen(true);
  };

  const handleNotificationsUpdate = async () => {
    await fetchUnreadCount();
  };

  const downloadTermsDocument = async (title: string) => {
    const fileName = title === 'agb' ? 'terms_&_conditions.pdf' : 'privacy_policy.pdf';
    try {
      const fileContent = await getTermsByType(title);
      if (!fileContent) {
        console.error('Download failed');
        return;
      }
      const url = window.URL.createObjectURL(fileContent);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('errors.document_downloading_failed'));
    } finally {
      setHideMenu(true);
    }
  };

  const mapOptionItems = (
    items: typeof menuOverlayItemsFirstSection | typeof menuOverlayItemsSecondSection,
    getOnClick: (item: any) => () => void,
  ) => {
    return items.map(item => ({
      ...item,
      onClick: getOnClick(item),
    }));
  };

  const getFirstSectionClickHandler = (item: (typeof menuOverlayItemsFirstSection)[number]) => {
    if (item.accessoryItem === 'notifications') {
      return () => openNotifications();
    }
    return () => onOptionSelect();
  };

  const getSecondSectionClickHandler = (item: (typeof menuOverlayItemsSecondSection)[number]) => {
    switch (item.type) {
      case 'terms':
        return () => downloadTermsDocument('agb');
      case 'policy':
        return () => downloadTermsDocument('company_registration');
      default:
        return () => onOptionSelect();
    }
  };
  return (
    <>
      <div className={classNames(styles.root, { [styles.expanded]: expanded }, `${hideMenu && 'hidden'}`)}>
        <OptionItemList items={mapOptionItems(filteredFirstSection, getFirstSectionClickHandler)} />
        <Divider className="mx-12 my-4" />
        <OptionItemList items={mapOptionItems(secondSection, getSecondSectionClickHandler)} />
        <Divider className="mx-12 my-4" />
        <OptionItemList items={[logoutMenuItem]} />
      </div>
      {isNotificationsModalOpen && (
        <div className="absolute top-[2rem] left-[25rem]">
          <NotificationCenterModal
            isOpen={isNotificationsModalOpen}
            onClose={() => setIsNotificationsModalOpen(false)}
            onNotificationsUpdate={handleNotificationsUpdate}
          />
        </div>
      )}
    </>
  );
};
